import { useState, useCallback } from 'react';

interface UseRetryOptions {
  maxAttempts?: number;
  delay?: number;
  backoff?: boolean;
}

export function useRetry(options: UseRetryOptions = {}) {
  const { maxAttempts = 3, delay = 1000, backoff = true } = options;
  const [isRetrying, setIsRetrying] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const retry = useCallback(async <T>(
    operation: () => Promise<T>
  ): Promise<T> => {
    setIsRetrying(true);
    let lastError: Error;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        setAttempts(attempt);
        const result = await operation();
        setIsRetrying(false);
        setAttempts(0);
        return result;
      } catch (error) {
        lastError = error as Error;
        
        if (attempt < maxAttempts) {
          const waitTime = backoff ? delay * Math.pow(2, attempt - 1) : delay;
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    setIsRetrying(false);
    setAttempts(0);
    throw lastError!;
  }, [maxAttempts, delay, backoff]);

  return { retry, isRetrying, attempts };
}